import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-w-xs rounded-md border border-border bg-popover px-2.5 py-1.5',
        'text-xs leading-relaxed text-popover-foreground shadow-lg',
        'data-[state=delayed-open]:animate-scale-in data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Suppress rendering entirely — e.g. a sidebar label only shown when collapsed. */
  disabled?: boolean;
}

/**
 * The common case in one component.
 *
 * A tooltip is never the only source of a control's name: every trigger that uses one
 * also carries an `aria-label`, because tooltips are unreachable by touch and by
 * several screen-reader navigation modes.
 */
export function Tooltip({ content, children, side = 'top', disabled = false }: TooltipProps) {
  if (disabled) return <>{children}</>;

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </TooltipRoot>
  );
}
